import { lazy, type ReactNode } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

/* eslint-disable react-refresh/only-export-components -- route config file with internal wrapper components */
import { AuthenticatedRoute } from '../allauth_auth/routing';
import DashboardLayout from '../components/DashboardLayout';
import { OrganizationsPage } from '../components/FullPageOrgSwitcher';
import { InternalGuard } from '../components/InternalGuard';
import { TeamGuard } from '../components/OrgGuard';
import { PortfolioGuard } from '../components/PortfolioGuard';
import { SeatGatedChat } from '../components/seat-gate/SeatGatedChat';
import { ViewerUpgradeScreen } from '../components/seat-gate/ViewerUpgradeScreen';
import ErrorPage from '../error-page';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { type FeatureName, useFeatureAccess } from '../hooks/usePermissions';
import { LazyRouteBoundary } from '../lib/components/LazyRouteBoundary';
import { JoinOnboardingRedirect } from '../lib/join-flow/components/JoinOnboardingRedirect';
import { PortfolioStandaloneLayout } from '../lib/portfolio/components/PortfolioStandaloneLayout';
import { defineRouteHealth, useViewHealthMarker } from '../lib/view-health';
import AccessPausedPage from '../pages/access-paused/AccessPausedPage';
import ScopePermissionsRequired from '../pages/auth/ScopePermissionsRequired';
import BillingRequiredPage from '../pages/billing-required/BillingRequiredPage';
import AutomationsPage from '../pages/crm/AutomationsPage';
import BookingsPage from '../pages/crm/BookingsPage';
import CompaniesList from '../pages/crm/CompaniesList';
import ContactsList from '../pages/crm/ContactsList';
import CRMObjectDetailPage from '../pages/crm/CRMObjectDetailPage';
import DealsList from '../pages/crm/DealsList';
import EmailThreadsPage from '../pages/crm/EmailThreadsPage';
import GenericCrmList from '../pages/crm/GenericCrmList';
import MeetingSummariesPage from '../pages/crm/MeetingSummariesPage';
import MeetingSummaryFullPage from '../pages/crm/MeetingSummaryFullPage';
import MeetingTypesPage from '../pages/crm/MeetingTypesPage';
import ActionDetailPage from '../pages/dashboard/ActionDetailPage';
import ActionsPage from '../pages/dashboard/ActionsPage';
import AgentFeedPage from '../pages/dashboard/AgentFeedPage';
import CreateTeam from '../pages/dashboard/CreateOrg';
import Dashboard from '../pages/dashboard/Dashboard';
import DashboardHome from '../pages/dashboard/DashboardHome';
import LearnedContext from '../pages/dashboard/LearnedContext';
import LegacyActionsPage from '../pages/dashboard/LegacyActionsPage';
import DesktopInstallPage from '../pages/desktop/install';
import NotFoundPage from '../pages/NotFoundPage';
import PortfolioPage from '../pages/portfolio/PortfolioPage';
import BuilderChat from '../pages/reasoning/BuilderChat';
import ConversationsHome from '../pages/reasoning/ConversationsHome';
import ConversationsManager from '../pages/reasoning/ConversationsManager';
import ReasoningChat from '../pages/reasoning/ReasoningChat';

const CalendarPage = lazy(() => import('../pages/calendar/CalendarPage'));
const ProfilePage = lazy(
  () => import('../pages/dashboard/profile/ProfilePage')
);
const UserTimelineDebug = lazy(
  () => import('../pages/dashboard/UserTimelineDebug')
);
const DebugPage = lazy(() => import('../pages/debug/DebugPage'));
const DashboardPage = lazy(() =>
  import('../pages/reports/dashboard/DashboardPage').then(m => ({
    default: m.DashboardPage,
  }))
);
const ReportBuilder = lazy(() =>
  import('../pages/reports/ReportBuilder').then(m => ({
    default: m.ReportBuilder,
  }))
);

// Redirect the old standalone Connected Accounts page into settings, where it
// now renders as a (hidden) section so it keeps the settings sidebar.
function SocialConnectionsRedirect() {
  const { orgId } = useParams();
  return <Navigate to={`/${orgId}/settings/connected-accounts`} replace />;
}

// Redirect old org detail route to new settings location
function OrganizationsRedirectToSettings() {
  const { targetTeamId } = useParams();
  return <Navigate to={`/${targetTeamId}/settings/team-management`} replace />;
}

// Redirect old onboarding route to dashboard
function OnboardingRedirectToDashboard() {
  const { orgId } = useParams();
  return <Navigate to={`/${orgId}`} replace />;
}

function MeetingTypeDetailRedirect() {
  const { orgId, id } = useParams();
  return <Navigate to={`/${orgId}/crm/ahoy_booking_type/${id}`} replace />;
}

function MeetingTypesRedirect() {
  const { orgId } = useParams();
  return <Navigate to={`/${orgId}/crm/booking-types`} replace />;
}

// Redirect legacy notes links (from owner-notification emails already sent,
// which point at /crm/notes — not an SPA route) to the canonical notes list.
// Must preserve location.search: slideoutEntityId is what opens the note.
function CrmNotesRedirect() {
  const { orgId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${orgId}/crm/ahoy-note${search}`} replace />;
}

/**
 * Selects between the two AI Actions shells.
 *
 * Both are registered under the single `actions/*` wildcard rather than as
 * competing route objects, for two reasons:
 *
 *  1. `dashboardRoutes` is a module-scope array and `createBrowserRouter` runs
 *     once in RouterWrapper's mount effect, so a hook value cannot select
 *     between two route arrays without rebuilding the router and destroying
 *     history state.
 *  2. The legacy shell REQUIRES a single continuously-mounted component across
 *     `/actions` -> `/actions/123` — its list scroll position, resize width and
 *     derived view all live in ProposalCenter state and reset on remount. It
 *     parses the URL itself from `location.pathname` and never reads the splat,
 *     so a wrapper element is transparent to it.
 *
 * The redesigned shell declares its own six paths as a descendant <Routes>.
 * Known tradeoff: descendant routes are invisible to `useMatches`, so it
 * inherits the union health handle below instead of a per-route split. That
 * resolves itself at flag removal, when these become real route objects.
 *
 * Flag off (the default, and what an unresolved flag yields) = legacy shell,
 * which is the shipped-and-known-good UI.
 */
function ActionsRouteSwitch() {
  const redesignEnabled = useFeatureFlag('ai_actions_redesign');

  if (!redesignEnabled) {
    return <LegacyActionsPage />;
  }

  return (
    <Routes>
      <Route index element={<ActionsPage />} />
      <Route path='done' element={<ActionsPage view='done' />} />
      <Route path='dismissed' element={<ActionsPage view='dismissed' />} />
      <Route path='archived' element={<ActionsPage view='archived' />} />
      <Route path='agent-feed' element={<AgentFeedPage />} />
      <Route path=':actionId' element={<ActionDetailPage />} />
    </Routes>
  );
}

/**
 * Gate that renders children when the org has the named feature, otherwise
 * renders the fallback. When `gatedMarker` is provided and the fallback is
 * shown, fires that marker into the active route health scope so the route
 * settles on its EMPTY variant instead of timing out.
 */
function FeatureGate({
  feature,
  fallback,
  children,
  gatedMarker,
}: {
  feature: FeatureName;
  fallback: ReactNode;
  children: ReactNode;
  gatedMarker?: string;
}) {
  const { hasFeature } = useFeatureAccess();
  const gated = !hasFeature(feature);
  useViewHealthMarker(gatedMarker ?? '', { ready: gated && !!gatedMarker });
  return gated ? fallback : children;
}

// Wrapper for DashboardPage that extracts route params
function DashboardPageWrapper() {
  const { orgId, dashboardId } = useParams();

  if (!orgId || !dashboardId) {
    return <Navigate to='/organizations' replace />;
  }

  return (
    <LazyRouteBoundary name='reports-dashboard'>
      <DashboardPage orgId={Number(orgId)} dashboardId={Number(dashboardId)} />
    </LazyRouteBoundary>
  );
}

export const dashboardRoutes = [
  // Organizations page (canonical route)
  {
    path: '/organizations',
    element: (
      <AuthenticatedRoute>
        <OrganizationsPage />
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Team selection route - redirect to canonical /organizations
  {
    path: '/select-organization',
    element: <Navigate to='/organizations' replace />,
  },
  // Team creation route (outside team context)
  {
    path: '/create-organization',
    element: (
      <AuthenticatedRoute>
        <CreateTeam />
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Standalone portfolio route for portfolio admins with no org membership of
  // their own (TeamGuard would bounce them). Org members reach the org-scoped
  // `/:orgId/portfolio` child route below, which renders inside the app shell.
  {
    path: '/portfolio',
    element: (
      <AuthenticatedRoute>
        <PortfolioGuard>
          <PortfolioStandaloneLayout>
            <PortfolioPage />
          </PortfolioStandaloneLayout>
        </PortfolioGuard>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Legacy redirect routes for backward compatibility
  {
    path: '/dashboard',
    element: (
      <AuthenticatedRoute>
        <TeamGuard>
          <Dashboard />
        </TeamGuard>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/dashboard/*',
    element: (
      <AuthenticatedRoute>
        <TeamGuard>
          <Dashboard />
        </TeamGuard>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Conversations routes (accessible without team context)
  {
    path: '/conversations',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <ConversationsHome />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/conversations/history',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <ConversationsManager />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/conversations/:conversationId',
    element: (
      <AuthenticatedRoute>
        <TeamGuard>
          <ReasoningChat />
        </TeamGuard>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
    handle: {
      health: defineRouteHealth({
        timeout: 30_000,
        success: {
          LOADED: ['conversation-data', 'polling-ready'],
        },
      }),
    },
  },
  {
    path: '/:orgId/permissions',
    element: (
      <AuthenticatedRoute>
        <ScopePermissionsRequired />
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Onboarding route - redirect to dashboard for backward compatibility
  {
    path: '/:orgId/onboarding',
    element: (
      <AuthenticatedRoute>
        <OnboardingRedirectToDashboard />
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // CRM routes (accessible without team context)
  {
    path: '/crm/contacts',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <ContactsList />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/crm/contacts/:recordId',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <CRMObjectDetailPage entityType='ahoy_contact' />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/crm/companies',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <CompaniesList />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/crm/companies/:recordId',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <CRMObjectDetailPage entityType='ahoy_company' />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/crm/deals',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <DealsList />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '/crm/deals/:recordId',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout>
          <CRMObjectDetailPage entityType='ahoy_deal' />
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Access-paused page for deactivated members.
  // MUST be registered before the generic '/:orgId' route so this specific path
  // wins matching. Wrapped in AuthenticatedRoute (login required) but NOT TeamGuard
  // — the user has no team access, so org-context hooks must not run.
  {
    path: '/:orgId/access-paused',
    element: (
      <AuthenticatedRoute>
        <AccessPausedPage />
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Billing-required page for orgs that need an active Stripe subscription.
  // MUST be registered before the generic '/:orgId' route so this specific
  // path wins matching. Wrapped in AuthenticatedRoute (login required) but
  // NOT TeamGuard — the user is gated and must reach this without org-context
  // hooks firing.
  {
    path: '/:orgId/billing-required',
    element: (
      <AuthenticatedRoute>
        <BillingRequiredPage />
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
  },
  // Generic organization route (must be last to avoid conflicts with specific routes)
  {
    path: '/:orgId',
    element: (
      <AuthenticatedRoute>
        <DashboardLayout skipGuard>
          <TeamGuard>
            <Dashboard />
          </TeamGuard>
        </DashboardLayout>
      </AuthenticatedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          <JoinOnboardingRedirect>
            <DashboardHome />
          </JoinOnboardingRedirect>
        ), // Team home - onboarding check happens in HomeRedirect at root
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['home-dashboard-ready'],
            },
          }),
        },
      },
      {
        path: 'dashboard',
        element: (
          <JoinOnboardingRedirect>
            <DashboardHome />
          </JoinOnboardingRedirect>
        ), // Dashboard content — same onboarding gate as the index alias
      },
      {
        path: 'portfolio',
        element: (
          <PortfolioGuard>
            <PortfolioPage />
          </PortfolioGuard>
        ),
      },
      {
        path: 'profile',
        element: (
          <LazyRouteBoundary name='profile'>
            <ProfilePage />
          </LazyRouteBoundary>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyRouteBoundary name='settings'>
            <ProfilePage />
          </LazyRouteBoundary>
        ),
      },
      {
        path: 'settings/:section',
        element: (
          <LazyRouteBoundary name='settings-section'>
            <ProfilePage />
          </LazyRouteBoundary>
        ),
        handle: {
          health: defineRouteHealth({
            // 20s (in-file norm is 15s): headroom for the lazy chunk fetch on
            // this route. Crashes still beacon immediately via the route
            // errorElement — LazyRouteBoundary rethrows non-chunk errors — so
            // the timeout only covers genuinely slow loads.
            timeout: 20_000,
            success: {
              LOADED: ['settings-section-ready'],
            },
            scopeName: (params: Readonly<Record<string, string>>) =>
              `/settings/${params.section}`,
          }),
        },
      },
      {
        path: 'learned-context',
        element: (
          <InternalGuard>
            <LearnedContext />
          </InternalGuard>
        ),
      },
      {
        path: 'user-timeline',
        element: (
          <InternalGuard>
            <LazyRouteBoundary name='user-timeline'>
              <UserTimelineDebug />
            </LazyRouteBoundary>
          </InternalGuard>
        ),
      },
      {
        path: 'organizations',
        element: <Navigate to='/organizations' replace />,
      },
      {
        path: 'organizations/:targetTeamId',
        element: <OrganizationsRedirectToSettings />,
      },
      {
        // Moved into settings so the page keeps the settings sidebar. Kept as a
        // redirect because OAuth callbacks and bookmarks still point here.
        path: 'social/connections',
        element: <SocialConnectionsRedirect />,
      },
      {
        path: 'conversations',
        element: (
          <FeatureGate
            feature='chat'
            fallback={<SeatGatedChat />}
            gatedMarker='chat-list-empty-ready'
          >
            <ConversationsHome />
          </FeatureGate>
        ),
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['chat-list-ready'],
              EMPTY: ['chat-list-empty-ready'],
            },
          }),
        },
      },
      {
        path: 'conversations/history',
        element: (
          <FeatureGate feature='chat' fallback={<SeatGatedChat />}>
            <ConversationsManager />
          </FeatureGate>
        ),
      },
      {
        // Builder conversations live under their own path segment rather than
        // `conversations/:id?builder=1`. The mode has to be readable
        // SYNCHRONOUSLY — `DashboardLayout` decides whether to render the
        // history nav and never loads the conversation — and a path segment is
        // part of the resource's identity, so navigation cannot drop it the way
        // a query param was dropped by the conversation list, a reload, and a
        // shared link.
        //
        // The conversation's `agent_type` remains the source of truth:
        // `ReasoningChat` reconciles the two and redirects when they disagree,
        // so a builder conversation opened at the ordinary route corrects
        // itself rather than silently rendering without its panel.
        path: 'build',
        element: (
          <FeatureGate feature='chat' fallback={<SeatGatedChat />}>
            <BuilderChat />
          </FeatureGate>
        ),
      },
      {
        path: 'build/:conversationId',
        element: (
          <FeatureGate
            feature='chat'
            fallback={<SeatGatedChat />}
            gatedMarker='build-gated-ready'
          >
            <ReasoningChat />
          </FeatureGate>
        ),
        handle: {
          health: defineRouteHealth({
            timeout: 30_000,
            success: {
              LOADED: ['conversation-data', 'polling-ready'],
              EMPTY: ['build-gated-ready'],
            },
          }),
        },
      },
      {
        path: 'conversations/:conversationId',
        element: (
          <FeatureGate
            feature='chat'
            fallback={<SeatGatedChat />}
            gatedMarker='conversation-gated-ready'
          >
            <ReasoningChat />
          </FeatureGate>
        ),
        // Note: This route inherits TeamGuard from parent, no DashboardLayout to hide nav bar
        handle: {
          health: defineRouteHealth({
            timeout: 30_000,
            success: {
              LOADED: ['conversation-data', 'polling-ready'],
              EMPTY: ['conversation-gated-ready'],
            },
          }),
        },
      },
      {
        path: 'actions/*',
        element: (
          <FeatureGate
            feature='ai_actions'
            fallback={<ViewerUpgradeScreen feature='ai_actions' />}
            gatedMarker='actions-list-empty-ready'
          >
            <ActionsRouteSwitch />
          </FeatureGate>
        ),
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              // List query resolved with ≥1 item AND the list panel committed
              // its rows. Fires for `/actions` URLs without a selected proposal.
              LOADED: ['actions-list-ready', 'actions-list-items-rendered'],
              // List query resolved with 0 items. Mutually exclusive with
              // LOADED — they can't both fire for the same navigation.
              EMPTY: ['actions-list-empty-ready'],
              // Fires for `/actions/:actionId` URLs: list loaded AND the
              // detail query resolved AND the detail panel rendered its
              // action cards. Strict superset of LOADED, so whichever set
              // completes first wins (LOADED if detail is still loading).
              PROPOSAL_LOADED: [
                'actions-list-ready',
                'actions-list-items-rendered',
                'proposal-detail-ready',
                'proposal-actions-rendered',
              ],
            },
          }),
        },
      },
      {
        path: 'reporting',
        element: (
          <LazyRouteBoundary name='reporting'>
            <ReportBuilder />
          </LazyRouteBoundary>
        ),
        handle: {
          health: defineRouteHealth({
            timeout: 30_000,
            success: {
              LOADED: ['cube-ready', 'data-loaded'],
            },
          }),
        },
      },
      {
        path: 'reporting/:reportId',
        element: (
          <LazyRouteBoundary name='reporting-detail'>
            <ReportBuilder />
          </LazyRouteBoundary>
        ),
        handle: {
          health: defineRouteHealth({
            timeout: 30_000,
            success: {
              LOADED: ['cube-ready', 'data-loaded'],
            },
          }),
        },
      },
      {
        path: 'calendar',
        element: <Navigate to='../meetings' replace />,
      },
      {
        path: 'meetings',
        element: (
          <LazyRouteBoundary name='calendar'>
            <CalendarPage />
          </LazyRouteBoundary>
        ),
        handle: {
          health: defineRouteHealth({
            // 20s (in-file norm is 15s): headroom for the lazy chunk fetch on
            // this route. Crashes still beacon immediately via the route
            // errorElement — LazyRouteBoundary rethrows non-chunk errors — so
            // the timeout only covers genuinely slow loads.
            timeout: 20_000,
            success: {
              LOADED: ['calendar-ready'],
            },
          }),
        },
      },
      {
        path: 'meeting-summaries',
        element: <MeetingSummariesPage />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['meeting-summaries-ready'],
              EMPTY: ['meeting-summaries-empty-ready'],
            },
          }),
        },
      },
      {
        path: 'meeting-summaries/:recordId',
        element: <MeetingSummaryFullPage />,
      },
      {
        path: 'desktop/install',
        element: <DesktopInstallPage />,
      },
      {
        path: 'crm/email-threads',
        element: <EmailThreadsPage />,
      },
      {
        path: 'crm/ahoy_email_thread/:recordId',
        element: <CRMObjectDetailPage entityType='ahoy_email_thread' />,
      },
      {
        path: 'dashboards',
        element: <Navigate to='reporting' replace />,
      },
      {
        path: 'dashboards/:dashboardId',
        element: <DashboardPageWrapper />,
        handle: {
          health: defineRouteHealth({
            timeout: 30_000,
            success: {
              LOADED: ['cube-ready', 'data-loaded'],
            },
          }),
        },
      },
      {
        path: 'crm/contacts',
        element: <ContactsList />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['contacts-list-ready'],
            },
          }),
        },
      },
      {
        path: 'crm/contacts/:recordId',
        element: <CRMObjectDetailPage entityType='ahoy_contact' />,
      },
      {
        path: 'crm/companies',
        element: <CompaniesList />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['companies-list-ready'],
            },
          }),
        },
      },
      {
        path: 'crm/companies/:recordId',
        element: <CRMObjectDetailPage entityType='ahoy_company' />,
      },
      {
        path: 'crm/meetings/:recordId',
        element: <CRMObjectDetailPage entityType='ahoy_meeting' />,
      },
      {
        path: 'crm/deals',
        element: <DealsList />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['deals-list-ready'],
            },
          }),
        },
      },
      {
        path: 'pipeline/deals/:pipelineId',
        element: <DealsList />,
        handle: {
          health: defineRouteHealth({
            timeout: 30_000,
            success: {
              LOADED: ['deals-list-ready'],
            },
          }),
        },
      },
      {
        path: 'pipeline/:entitySlug/:pipelineId',
        element: <GenericCrmList />,
      },
      {
        path: 'crm/deals/:recordId',
        element: <CRMObjectDetailPage entityType='ahoy_deal' />,
      },
      {
        path: 'crm/booking-types',
        element: <MeetingTypesPage />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['meeting-types-list-ready'],
            },
          }),
        },
      },
      {
        path: 'crm/meeting-types',
        element: <MeetingTypesRedirect />,
      },
      {
        path: 'crm/ahoy_booking_type/:recordId',
        element: <CRMObjectDetailPage entityType='ahoy_booking_type' />,
      },
      {
        path: 'crm/ahoy_meeting_type/:id',
        element: <MeetingTypeDetailRedirect />,
      },
      {
        path: 'crm/bookings',
        element: <BookingsPage />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['bookings-list-ready'],
            },
          }),
        },
      },
      {
        path: 'crm/notes',
        element: <CrmNotesRedirect />,
      },
      {
        path: 'crm/:entitySlug/:recordId',
        element: <CRMObjectDetailPage />,
      },
      {
        path: 'crm/:entitySlug',
        element: <GenericCrmList />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['crm-entity-list-ready'],
            },
            failure: ['crm-entity-type-not-found'],
          }),
        },
      },
      {
        // Automations are a thing you build, not a setting — so they get a
        // top-level route rather than living under /settings. The friendly
        // path is deliberate: /automations, not /crm/ahoy_event_automation.
        path: 'automations',
        element: <AutomationsPage />,
        handle: {
          health: defineRouteHealth({
            timeout: 15_000,
            success: {
              LOADED: ['automations-list-ready'],
            },
          }),
        },
      },
      {
        path: 'debug',
        element: (
          <InternalGuard>
            <LazyRouteBoundary name='debug'>
              <DebugPage />
            </LazyRouteBoundary>
          </InternalGuard>
        ),
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
